import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CustomVerticalIcon } from "@/components/crayons-code-logo/CustomIcon.vertical";
import { authInstance } from "@/lib/axios";
import { applyServerErrors } from "@/lib/apply-server-errors";
import {
  forgotPasswordSchema,
  type ForgotPasswordFormValues,
  FORGOT_PASSWORD_FIELDS,
} from "@/schemas/auth";

export const Route = createFileRoute("/(auth)/forgot-password")({
  component: ForgetPassword,
});

function ForgetPassword() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const onValid = async (data: ForgotPasswordFormValues) => {
    setIsSubmitting(true);
    try {
      await authInstance.post("/api/auth/forgot-password/", data);
      toast.success("Password reset email sent. Please check your inbox.");
    } catch (err) {
      const { handled, rest } = applyServerErrors(
        err,
        setError,
        FORGOT_PASSWORD_FIELDS,
      );
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to send password reset email. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-svh bg-muted">
      {/* Left side - Form */}
      <div className="flex flex-col gap-4 p-6 md:p-10 h-screen">
        {/* Form Container */}
        <div className="flex flex-1 flex-col gap-6 items-center justify-center">
          <h1>
            <div className="flex items-center justify-center gap-2 font-medium text-xl pb-2">
              <CustomVerticalIcon size={180} />
            </div>
          </h1>
          <Card className="w-full max-w-sm text-center shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">
                Forgot Password
              </CardTitle>
              <CardDescription>
                Enter your email address to receive a password reset link.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onValid)} className="space-y-4">
                {/* Email Field */}
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="m@example.com"
                    autoComplete="email"
                    {...register("email")}
                  />
                  {errors.email ? (
                    <p className="text-xs text-red-500">{errors.email.message}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      We'll send a reset link to this email.
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Send Reset Link"}
                </Button>
              </form>
            </CardContent>
          </Card>
          <div className="text-muted-foreground text-center text-xs text-balance">
            By clicking continue, you agree to our{" "}
            <Link
              to="/terms-of-service"
              className="underline underline-offset-4 hover:text-primary"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacy-policy"
              className="underline underline-offset-4 hover:text-2rimary"
            >
              Privacy Policy
            </Link>
            .
          </div>
        </div>
      </div>
    </div>
  );
}
